import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import {
  ApplicationType,
  TranslationVersion,
  S3Environment,
  RollbackResult,
} from '../types/translation-types';

interface VersionStore {
  // Data state
  versions: TranslationVersion[];
  totalCount: number;
  selectedVersion: TranslationVersion | null;
  lastRollbackResult: RollbackResult | null;

  // Filter state
  selectedAppType: ApplicationType;
  environmentFilter: S3Environment | null;

  // Loading states
  isLoading: boolean;
  isRollingBack: boolean;

  // Error states
  error: string | null;
  rollbackError: string | null;

  // Actions
  fetchVersions: () => Promise<void>;
  rollback: (versionId: number, reason: string) => Promise<RollbackResult>;
  setSelectedAppType: (appType: ApplicationType) => void;
  setEnvironmentFilter: (environment: S3Environment | null) => void;
  setSelectedVersion: (version: TranslationVersion | null) => void;
  clearError: () => void;
  clearRollbackError: () => void;
  reset: () => void;
}

const initialState = {
  versions: [],
  totalCount: 0,
  selectedVersion: null,
  lastRollbackResult: null,
  selectedAppType: ApplicationType.ReactWeb,
  environmentFilter: null,
  isLoading: false,
  isRollingBack: false,
  error: null,
  rollbackError: null,
};

export const useVersionStore = create<VersionStore>((set, get) => ({
  ...initialState,

  fetchVersions: async () => {
    const state = get();
    if (state.isLoading) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const services = servicesContainer.getServices();
      const response = await services.getVersionsUseCase.execute({
        applicationType: state.selectedAppType,
        environment: state.environmentFilter ?? undefined,
      });

      set({
        versions: response.value,
        totalCount: response['@odata.count'] ?? response.value.length,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('VersionStore.fetchVersions: Failed:', error);
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load version history',
      });
    }
  },

  rollback: async (versionId: number, reason: string) => {
    set({ isRollingBack: true, rollbackError: null });

    try {
      const services = servicesContainer.getServices();
      const result = await services.rollbackVersionUseCase.execute(versionId, reason);

      // Refresh versions after rollback
      await get().fetchVersions();

      set({
        isRollingBack: false,
        lastRollbackResult: result,
        rollbackError: null,
      });

      return result;
    } catch (error) {
      console.error(`VersionStore.rollback(${versionId}): Failed:`, error);
      set({
        isRollingBack: false,
        rollbackError: error instanceof Error ? error.message : 'Failed to rollback version',
      });
      throw error;
    }
  },

  setSelectedAppType: (appType: ApplicationType) => {
    set({ selectedAppType: appType, versions: [], totalCount: 0 });
  },

  setEnvironmentFilter: (environment: S3Environment | null) => {
    set({ environmentFilter: environment });
  },

  setSelectedVersion: (version: TranslationVersion | null) => {
    set({ selectedVersion: version });
  },

  clearError: () => set({ error: null }),

  clearRollbackError: () => set({ rollbackError: null }),

  reset: () => set(initialState),
}));
